import { Box, Flex, Icon, Stack, Text } from '@chakra-ui/core';
import { motion } from 'framer-motion';
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { RiQuestionnaireFill } from 'react-icons/ri';
import { VideoPreviewModal } from '../../../../../app/components/VideoPreviewModal';
import { VideoTrigger } from '../../../../../app/components/VideoTrigger';
import colors from '../../../../../styles/colors';
import Label from '../../../../common/Label/Label';
import { EmptyQuestionsView } from '../questions/EmptyQuestionsView';
import { QuestionType } from '../questions/QuestionType';
import { questionsViewWrapper } from '../specificQuestions/Questions.styles';
import VideoResponsePresenter from './videoResponsePresenter';

const MotionBox = motion.custom(Box);
const MotionStack = motion.custom(Stack);

const parentVariants = {
  visible: { transition: { staggerChildren: 0.07, delayChildren: 0.05 } },
  hidden: { transition: { staggerChildren: 0.01, staggerDirection: -1 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } },
};

interface BaseQuestionViewProps {
  activeQuestionType: QuestionType;
  hasNoQuestionOnPosition: boolean;
}
interface VideoResponseQuestionsViewProps extends BaseQuestionViewProps {
  questions: VideoResponsePresenter | undefined;
  initialized: boolean;
}
export const VideoResponseQuestionsView: React.FC<VideoResponseQuestionsViewProps> = ({
  questions,
  hasNoQuestionOnPosition,
  activeQuestionType,
  initialized,
}) => {
  const { t } = useTranslation();

  if (!initialized) {
    return null;
  }
  const total = questions?.getQuestionsCount() ?? 0;
  const answered = questions?.getAnsweredQuestionsCount() ?? 0;

  if (hasNoQuestionOnPosition || total === 0) {
    if (activeQuestionType === QuestionType.ALL_QUESTIONS) return null;
    return <EmptyQuestionsView message={t('questions.noVideoSubmitted', 'This position has no Video Response Questions')} />;
  }

  return (
    <Fragment>
      <MotionStack
        key="VideoQuestionsMotionStack"
        initial="hidden"
        animate="visible"
        variants={parentVariants}
        spacing={0}
        padding={2}
        css={questionsViewWrapper}
        data-testid="VideoResponseQuestions"
      >
        {total > 0 && (
          <Flex justify="flex-start" align="baseline" mb={2}>
            <Label color={colors.gray['500']} colorDarkMode={colors.gray['600']} fontWeight="600">
              {t('questions.totalQuestionAnswered', { count: answered, total })}
            </Label>
          </Flex>
        )}

        {questions?.questions.map((videoQuestion, index) => {
          const { id, answer, question, skipped } = videoQuestion;
          const hasVideo = answer != null && answer !== '';

          return (
            <MotionBox key={id} variants={cardVariants} mb={4}>
              <Box
                border="1.5px dashed"
                borderColor={skipped ? colors.californiaGold : 'gray.200'}
                borderRadius="12px"
                bg="white"
                overflow="hidden"
              >
                {/* Question section */}
                <Box
                  p={4}
                  borderRadius="12px 12px 0 0"
                  bg={skipped ? '#F7F4E8' : 'white'} // beige bg for skipped question
                >
                  <Text fontSize="sm" fontWeight="400" color="gray.700" mb={hasVideo && !skipped ? 3 : 0} lineHeight="1.6">
                    {`${index + 1}. ${question}`}
                  </Text>

                  {/* Video thumbnail */}
                  {hasVideo && !skipped && (
                    <Box display="flex" justifyContent="center">
                      <Box width="100%">
                        <VideoPreviewModal
                          video={{ id, url: answer, label: question }}
                          getVideoKey={(video) => video?.id ?? ''}
                          getVideoSrc={(video) => video?.url ?? ''}
                          getVideoAlt={(video) => video?.label ?? ''}
                          setTrigger={<VideoTrigger src={answer} />}
                        />
                      </Box>
                    </Box>
                  )}

                  {/* No video uploaded */}
                  {!hasVideo && !skipped && (
                    <Text fontSize="sm" textAlign="left" borderRadius="14px" style={{ color: '#000', opacity: '0.2' }}>
                      {t('videoResponseQuestions.unanswered')}
                    </Text>
                  )}
                </Box>

                {/* Skipped section */}
                {skipped && (
                  <Box display="flex" alignItems="center" justifyContent="center" py={4} px={4}>
                    <Icon as={RiQuestionnaireFill} color={colors.californiaGold} size={24} />
                    <Text fontSize="sm" fontWeight="bold" color="#2D3748" ml={2}>
                      {t('videoResponseQuestions.skipped')}
                    </Text>
                  </Box>
                )}
              </Box>
            </MotionBox>
          );
        })}
      </MotionStack>
    </Fragment>
  );
};
